import { useTheme } from '../lib/theme-provider';
import { Crown, UserPlus, X } from 'lucide-react';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  isHost: boolean;
  isReady: boolean;
  isOnline: boolean;
  avatar?: string;
}

interface PlayerSeatGridProps {
  players: Player[];
  maxPlayers: number;
  currentUserId?: string;
  isHost: boolean;
  isSpectator: boolean;
  onSitDown?: (seatNumber: number) => void;
  onKickPlayer?: (playerId: string) => void;
  onTransferHost?: (playerId: string) => void;
}

export default function PlayerSeatGrid({
  players,
  maxPlayers,
  currentUserId,
  isHost,
  isSpectator,
  onSitDown,
  onKickPlayer,
  onTransferHost,
}: PlayerSeatGridProps) {
  const { theme } = useTheme();

  const getSeatStatus = (seatNumber: number) => {
    const player = players.find((p) => p.seatNumber === seatNumber);
    if (!player) return 'empty';
    if (!player.isOnline) return 'offline';
    return 'online';
  };

  const getPlayerBySeat = (seatNumber: number) => {
    return players.find((p) => p.seatNumber === seatNumber);
  };

  // 计算需要多少行
  const rows = Math.ceil(maxPlayers / 3);

  return (
    <div
      className="rounded-xl p-4"
      style={{ backgroundColor: theme.colors['bg-card'] }}
    >
      {/* 标题 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown size={18} style={{ color: theme.colors['accent-gold'] }} />
          <span
            className="font-bold"
            style={{ color: theme.colors['text-primary'] }}
          >
            玩家座位
          </span>
        </div>
        <span style={{ color: theme.colors['text-secondary'] }}>
          {players.filter((p) => p.seatNumber > 0).length} / {maxPlayers} 已就位
        </span>
      </div>

      {/* 座位网格 - 固定3列，每行最多3个 */}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-3">
            {Array.from({ length: 3 }).map((_, colIndex) => {
              const seatNumber = rowIndex * 3 + colIndex + 1;
              if (seatNumber > maxPlayers) return null;

              const player = getPlayerBySeat(seatNumber);
              const status = getSeatStatus(seatNumber);
              const isCurrentUser = player?.id === currentUserId;

              return (
                <div
                  key={seatNumber}
                  onClick={() => {
                    if (status === 'empty' && !isSpectator && onSitDown) {
                      onSitDown(seatNumber);
                    }
                  }}
                  className={`
                    relative flex flex-col items-center justify-center
                    w-[100px] h-[100px] rounded-xl transition-all
                    ${status === 'empty' && !isSpectator ? 'cursor-pointer hover:scale-105' : ''}
                  `}
                  style={{
                    backgroundColor:
                      status === 'online'
                        ? isCurrentUser
                          ? `${theme.colors['accent-gold']}20`
                          : theme.colors['bg-primary']
                        : `${theme.colors['bg-primary']}60`,
                    border: `2px solid ${
                      status === 'online'
                        ? isCurrentUser
                          ? theme.colors['accent-gold']
                          : theme.colors['border-secondary']
                        : `${theme.colors['border-secondary']}40`
                    }`,
                    opacity: status === 'offline' ? 0.6 : 1,
                  }}
                >
                  {/* 座位号 */}
                  <span
                    className="absolute top-2 left-2 text-xs font-bold"
                    style={{ color: theme.colors['text-muted'] }}
                  >
                    #{seatNumber}
                  </span>

                  {status === 'empty' ? (
                    // 空座位
                    <>
                      <UserPlus
                        size={24}
                        style={{ color: `${theme.colors['text-secondary']}60` }}
                      />
                      <span
                        className="text-xs mt-1"
                        style={{ color: `${theme.colors['text-secondary']}60` }}
                      >
                        {isSpectator ? '空位' : '点击就位'}
                      </span>
                    </>
                  ) : (
                    // 有玩家
                    <>
                      {/* 头像 - 优先显示上传的头像 */}
                      {player?.avatar ? (
                        <img
                          src={player.avatar}
                          alt={player.nickname}
                          className="w-12 h-12 rounded-full object-cover mb-1"
                          onError={(e) => {
                            // 头像加载失败时显示首字母
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.className = 'w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-1';
                              fallback.style.backgroundColor = isCurrentUser
                                ? theme.colors['accent-gold']
                                : theme.colors['accent-purple'];
                              fallback.style.color = theme.colors['bg-primary'];
                              fallback.textContent = player.nickname?.[0] || '?';
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        // 无头像时显示昵称首字母
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-1"
                          style={{
                            backgroundColor: isCurrentUser
                              ? theme.colors['accent-gold']
                              : theme.colors['accent-purple'],
                            color: theme.colors['bg-primary'],
                          }}
                        >
                          {player?.nickname?.[0] || '?'}
                        </div>
                      )}

                      {/* 昵称 */}
                      <span
                        className="text-sm font-medium truncate w-full text-center px-1"
                        style={{ color: theme.colors['text-primary'] }}
                      >
                        {player?.nickname}
                      </span>

                      {/* 房主标识 */}
                      {player?.isHost && (
                        <span
                          className="absolute top-2 right-2 text-xs px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: theme.colors['accent-gold'],
                            color: theme.colors['bg-primary'],
                          }}
                        >
                          房主
                        </span>
                      )}

                      {/* 离线标识 */}
                      {status === 'offline' && (
                        <span
                          className="absolute top-2 right-2 text-xs px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: theme.colors['text-muted'],
                            color: theme.colors['bg-primary'],
                          }}
                        >
                          离线
                        </span>
                      )}

                      {/* 准备标识 */}
                      {player?.isReady && status === 'online' && (
                        <span
                          className="absolute bottom-2 right-2 w-3 h-3 rounded-full"
                          style={{ backgroundColor: '#4ade80' }}
                        />
                      )}

                      {/* 踢人按钮（仅房主可见） */}
                      {isHost &&
                        !player?.isHost &&
                        onKickPlayer &&
                        status === 'online' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onKickPlayer(player.id);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                            style={{
                              backgroundColor: theme.colors['accent-red'],
                              color: theme.colors['bg-primary'],
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
